import React, { useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Upload, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import usePersonnelFile from '../../components/personnel-file/usePersonnelFile';
import PersonnelFileView from '../../components/personnel-file/PersonnelFileView';
import useMyDocumentsApi from './config';
import { DOCUMENT_ACCEPT } from '../../utils/documentAccept';
import DocumentViewerModal from '../../components/ui/DocumentViewerModal';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';

const Container = styled.div``;

// Sonuç şeridi — EmployeeDocumentsPage'in FilterBanner'ıyla AYNI ölçüler ve tipografi,
// yalnız renk semantiği değişiyor (başarı yeşil, hata kırmızı).
const Feedback = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ $kind }) => ($kind === 'success' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(229, 57, 53, 0.08)')};
  border: 1.5px solid ${({ theme, $kind }) => (
    $kind === 'success' ? theme.colors.auxiliar.success : theme.colors.auxiliar.danger
  )};
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  padding: 10px 14px;
  margin-bottom: 12px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Spinner = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: ${({ theme }) => theme.colors.primary.main};

  svg { animation: my-doc-spin 0.9s linear infinite; }
  @keyframes my-doc-spin { to { transform: rotate(360deg); } }
`;
const Heading = styled.h2`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 14px;
`;

// Belgelerim — çalışanın kendi özlük dosyasının self-servis görünümü.
// Kimlik oturumdan gelir (user.employeeId), URL parametresinden DEĞİL: backend zaten
// sahiplik kontrolü yapıyor (EM.Write olmayan çağıran yalnız kendi dosyasını görür), ama
// bu ekranın kendisi başka bir çalışanın id'sini isteyebilecek bir rota parametresi hiç
// almaz — self-servis yüzeyin tanımı gereği.
export default function MyDocumentsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const employeeId = user?.employeeId;
  const { fetching, file, refresh } = usePersonnelFile(employeeId);
  const { uploadDocument } = useMyDocumentsApi();

  const inputRef = useRef(null);
  const [targetId, setTargetId] = useState(null);
  // Yüklemesi süren satırın id'si — o satırın aksiyonu spinner'a döner ve tıklanamaz olur.
  const [uploadingId, setUploadingId] = useState(null);
  // { kind: 'success' | 'error', text } | null — sayfa başında tek bir sonuç şeridi.
  const [feedback, setFeedback] = useState(null);
  // { fileUrl, title } | null — açık olduğunda DocumentViewerModal'ı besler.
  const [viewing, setViewing] = useState(null);

  // Yükleme sonucunun İZLENEBİLİR olması (müşteri bildirimi 2026-08-18): önceki hâlde bu
  // fonksiyon ne yüklenme sırasında bir şey gösteriyor ne de hata hâlinde. `uploadDocument`
  // içindeki presign/PUT/POST üçlüsünden herhangi biri fırlattığında istisna buraya kadar
  // gelip yakalanmadan kayboluyordu: kullanıcı dosyayı seçiyor ve ekranda HİÇBİR ŞEY olmuyordu
  // (canlıda gerçekten böyleydi — özel kovada CORS tanımlı olmadığı için tarayıcı PUT'u
  // engelliyordu). Artık üç durum da görünür: yükleniyor / başarılı / hatalı.
  const handlePicked = useCallback(async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = '';
    const id = targetId;
    setTargetId(null);
    if (!picked || !id) return;

    setFeedback(null);
    setUploadingId(id);
    try {
      const ok = await uploadDocument(id, picked);
      if (ok) {
        setFeedback({ kind: 'success', text: t('myDocuments.uploadSuccess', { name: picked.name }) });
        refresh();
      } else {
        setFeedback({ kind: 'error', text: t('myDocuments.uploadFailed') });
      }
    } catch {
      // Ağ/CORS/depolama hatası: axios burada fırlatır. Sessiz kalmak, kullanıcıya "yükledim"
      // yanılgısı yaşatır — belgesi eksik kalır ve bunu asla öğrenemez.
      setFeedback({ kind: 'error', text: t('myDocuments.uploadFailed') });
    } finally {
      setUploadingId(null);
    }
  }, [targetId, uploadDocument, refresh, t]);

  // Self-servis yükleme YALNIZ istihdam belgelerinde (`source === 'employment'`) ve yalnız
  // henüz karşılanmamış/reddedilmiş satırlarda (`status` pending|rejected) sunulur. Diğer
  // aileler (işe alım, işe hazırlık, sertifika, ayrılış) kendi akışlarında yönetilir — burada
  // yükleme butonu göstermek /v1/employee-document-requirements/{id}/upload'un zaten
  // reddedeceği bir aksiyonu kullanıcıya sunmak olurdu.
  const renderRowAction = useCallback((item) => {
    const uploadable = item.source === 'employment'
      && (item.status === 'pending' || item.status === 'rejected');
    if (!uploadable) return null;

    if (uploadingId === item.id) {
      return (
        <Tooltip text={t('myDocuments.uploading')}>
          <Spinner data-testid="upload-spinner" aria-live="polite">
            <Loader2 size={16} />
          </Spinner>
        </Tooltip>
      );
    }

    return (
      <Tooltip text={t('myDocuments.upload')}>
        <IconButton
          data-testid="upload-action"
          disabled={!!uploadingId}
          onClick={() => { setTargetId(item.id); inputRef.current?.click(); }}
        >
          <Upload size={16} />
        </IconButton>
      </Tooltip>
    );
  }, [t, uploadingId]);

  return (
    <Container>
      <Heading>{t('myDocuments.title')}</Heading>
      {feedback && (
        <Feedback $kind={feedback.kind} role="status" data-testid="upload-feedback">
          {feedback.kind === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          <span>{feedback.text}</span>
        </Feedback>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={DOCUMENT_ACCEPT}
        style={{ display: 'none' }}
        onChange={handlePicked}
      />
      <PersonnelFileView
        items={file?.items}
        fetching={fetching}
        onOpenFile={(item) => setViewing({
          fileUrl: item.fileUrl,
          title: t(`personnelFile.type.${item.documentType}`, { defaultValue: item.title }),
        })}
        renderRowAction={renderRowAction}
      />
      <DocumentViewerModal
        open={!!viewing}
        fileUrl={viewing?.fileUrl}
        title={viewing?.title}
        onClose={() => setViewing(null)}
      />
    </Container>
  );
}
